<template>
  <div>
    <form @submit.prevent="" class="default-form">
      <div class="d-flex flex-column flex-md-row justify-content-between mb-5">
        <!-- TYPE -->
        <div class="col-3 form-group mb-3 me-2">
          <label class="form-label fw-bold w-100 d-block">Tipo de estratégia*</label>
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              :id="`type-display`"
              value="DISPLAY"
              v-model="form.type"
              :disabled="isEditPage"
            >
            <label class="form-check-label" :for="`type-display`">Display</label>
          </div>
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              :id="`type-video`"
              value="DISPLAY_VIDEO"
              v-model="form.type"
              :disabled="isEditPage"
            >
            <label class="form-check-label" :for="`type-video`">Vídeo</label>
          </div>
        </div> <!-- END TYPE -->
        <!-- NAME -->
        <div class="form-group mb-5 col-12 col-md-9">
          <label class="form-label fw-bold">{{ $t('strategy_name') }}*</label>
          <input type="text" class="form-control" :placeholder="$t('strategy_name')" v-model.trim="form.name" />
        </div> <!-- END NAME -->
      </div>
      <div class="separator separator-dashed border-color10 my-5"></div>
      <!-- LOCATION -->
      <div>
        <h3 class="text-uppercase font-size-regular fw-500 mb-5">
          <span>{{ $t('campaign_geography') }}</span>
        </h3>
        <div class="text-center">
          <p>{{ $t('campaign_geography_description') }}</p>
          <div class="d-flex justify-content-center">
            <div class="me-5">
              <Button
                type="button"
                class="btn-geography-select"
                :class="{
                  'selected': !useMap
                }"
                @click="toggleMap(false)"
              >
                <span class="title fw-bold d-block">{{ $t('campaign_geography_select_region_title') }}</span>
                <span class="description">{{ $t('campaign_geography_select_region_description') }}</span>
              </Button>
            </div>
            <div>
              <Button
                type="button"
                class="btn-geography-select"
                :class="{
                  'selected': useMap
                }"
                @click="toggleMap(true)"
              >
                <span class="title fw-bold d-block">{{ $t('campaign_geography_select_radius_title') }}</span>
                <span class="description">{{ $t('campaign_geography_select_radius_description') }}</span>
              </Button>
            </div>
          </div>
          <div class="mt-6 d-flex justify-content-center">
            <!-- REGION -->
            <div
              class="col-12 col-md-6 text-start pe-lg-4"
              :class="{
                'geography-container-disabled': useMap
              }"
            >
              <div class="form-group mb-3 me-2">
                <div class="d-flex align-items-center mb-2">
                  <label class="form-label fw-bold mb-0">
                    {{ $t('countries') }}
                    <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                  </label>
                </div>
                <template v-if="countriesLoader">
                  <Loader
                    :size="'small'"
                    :align="'start'"
                  />
                </template>
                <template v-else>
                  <Multiselect
                    v-model="selectedCountries"
                    :placeholder="$t('search2')"
                    label="label"
                    track-by="code"
                    :options="countryOptions"
                    :multiple="true"
                  />
                </template>
              </div> <!-- END COUNTRIES -->
              <!-- STATES -->
              <div class="form-group me-2" v-if="selectedCountries.length > 0">
                <div class="d-flex align-items-center mb-2">
                  <label class="form-label fw-bold mb-0 py-1">
                    {{ $t('states') }}
                    <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                  </label>
                  <Button
                    class="btn-xs btn-color10 ms-2 py-1"
                    @click="selectAllStates"
                    v-if="stateOptions.length > 0"
                  >
                    {{ $t('select_all') }}
                  </Button>
                  <Button
                    class="btn-xs btn-color10 ms-2 py-1"
                    @click="clearSelection('states')"
                    v-if="form.states.length > 0"
                  >
                    {{ $t('clear') }}
                  </Button>
                </div>
                <template v-if="statesLoader">
                  <Loader size="small" :align="'start'" />
                </template>
                <template v-else>
                  <Multiselect
                    v-model="form.states"
                    :placeholder="$t('search2')"
                    label="label"
                    track-by="code"
                    :options="stateOptions"
                    :multiple="true"
                    :disabled="selectedCountries.length == 0"
                  />
                  <div
                    class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2"
                    v-if="selectedCountries.length == 0"
                  >
                    <p
                      class="mb-0"
                    >
                      {{ $t('select_at_least_one_country_for_state_selection') }}
                    </p>
                  </div>
                </template>
              </div> <!-- END STATES -->
              <!-- CITIES -->
              <div class="form-group" v-if="selectedCountries.length > 0">
                <label class="form-label fw-bold py-1">
                  {{ $t('cities') }}
                  <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                </label>
                <template v-if="citiesLoader">
                  <Loader size="small" :align="'start'" />
                </template>
                <template v-else>
                  <Multiselect
                    v-model="form.cities"
                    :placeholder="$t('search2')"
                    label="label"
                    track-by="code"
                    :options="citiesOptions"
                    :multiple="true"
                    :disabled="selectedCountries.length == 0"
                  />
                  <div
                    class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2"
                    v-if="selectedCountries.length == 0"
                  >
                    <p
                      class="mb-0"
                    >
                      {{ $t('select_at_least_one_country_for_city_selection') }}
                    </p>
                  </div>
                </template>
              </div> <!-- END CITIES -->
              <div v-if="showAlertOnCitiesAndStatesSelection && (!countriesLoader && !citiesLoader && !statesLoader)">
                <p class="fw-bold alert-message-card text-color2 font-size-xRegular mt-2" v-html="showAlertOnCitiesAndStatesSelection">
                </p>
              </div>
            </div> <!-- END REGION -->
            <!-- RADIUS -->
            <div
              class="col-12 col-md-6 text-start ps-4"
              :class="{
                'geography-container-disabled': !useMap
              }"
            >
              <!-- GOOGLE MAPS -->
              <div class="form-group mb-3 col-12 col-lg-12 me-2">
                <div class="d-flex align-items-center mb-2">
                  <GoogleMapsComponent
                    :elements="form.marker_locations"
                    :show-chips="true"
                    :use-places-api="true"
                    :inventory-management="false"
                    @update-radius="updateRadius"
                    @update-radius-location="updateRadiusLocation"
                    @update-marker-locations="updateMarkerLocations"
                  ></GoogleMapsComponent>
                </div>
              </div> <!-- END MAPS -->
            </div> <!-- END RADIUS -->
          </div>
        </div>
      </div>
      <!-- END LOCATION -->

      <div class="separator separator-dashed border-color10 my-5"></div>
      <div class="d-flex flex-column flex-md-row justify-content-between">
        <div class="col-12 col-md-6 pe-5 mb-5">
          <h3 class="text-uppercase font-size-regular fw-500 mb-3">
            <span>{{ $t('audience_segmentation') }}</span>
          </h3>
          <!-- GENDER -->
          <div class="form-group mb-5 me-2">
            <label class="form-label fw-bold w-100 d-block">{{ $t('gender') }}* <span v-b-tooltip.hover.html :title="$t('tooltips.demographic_data')"><i class="fa fa-info-circle font-size-xRegular"></i></span></label>
            <div class="form-check form-check-inline mb-2">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`gender-male`"
                value="MALE"
                v-model="form.demographics.gender"
              >
              <label class="form-check-label" :for="`gender-male`">{{ $t('male') }}</label>
            </div>
            <div class="form-check form-check-inline mb-2">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`gender-female`"
                value="FEMALE"
                v-model="form.demographics.gender"
              >
              <label class="form-check-label" :for="`gender-female`">{{ $t('female') }}</label>
            </div>
            <div class="form-check form-check-inline mb-2">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`gender-unknown`"
                value="UNKNOWN"
                v-model="form.demographics.gender"
              >
              <label class="form-check-label" :for="`gender-unknown`">{{ $t('unknown') }}</label> <span v-b-tooltip.hover :title="$t('info_unknown')"><i class="fa fa-question-circle font-size-small"></i></span>
            </div>
          </div><!-- END GENDER -->
          <!-- AGE -->
          <div class="form-group mb-20 me-2 d-flex">
            <div class="col-7 me-10">
              <label class="form-label fw-bold w-100 d-block">{{ $t('age') }}* <span v-b-tooltip.hover.html :title="$t('tooltips.demographic_data')"><i class="fa fa-info-circle font-size-xRegular"></i></span></label>
              <div class="position-relative h-100">
                <AgeSlider v-model="form.demographics.age" />
              </div>
            </div>
            <div class="form-check mt-8">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`age-unknown`"
                value="UNKNOWN"
                v-model="form.demographics.age_unknown"
              >
              <label class="form-check-label" :for="`age-unknown`">{{ $t('unknown') }}</label> <span v-b-tooltip.hover :title="$t('info_unknown')"><i class="fa fa-question-circle font-size-small"></i></span>
            </div>
          </div><!-- END AGE -->
          <!-- CATEGORIES -->
          <div class="form-group mb-3">
            <label class="form-label fw-bold">{{ $t('categories2') }} <span v-b-tooltip.hover.html :title="$t('select_broad_categories')"><i class="fa fa-info-circle font-size-xRegular"></i></span></label>
            <div v-if="categoriesLoader">
              <Loader
                :size="'small'"
                :align="'start'"
              />
            </div>
            <div v-else>
              <Multiselect
                id="categories"
                v-model="form.categories"
                :placeholder="$t('search2')"
                label="label"
                track-by="code"
                :options="categoriesOptions"
                :multiple="true"
              />
            </div>
          </div><!-- CATEGORIES -->
          <!-- KEYWORDS -->
          <div class="form-group mb-3">
            <label class="form-label fw-bold w-100 d-block">{{ $t('keywords') }} <span v-b-tooltip.hover.html :title="$t('select_keywords_description')"><i class="fa fa-info-circle font-size-xRegular"></i></span></label>
            <Multiselect
              id="tagging"
              v-model="form.keywords"
              tag-placeholder="Add this as new keywords"
              placeholder="Add a keywords"
              label="label"
              track-by="code"
              :options="keywordsOptions"
              :multiple="true"
              :taggable="true"
              @tag="addKeywords"
            />
          </div><!-- KEYWORDS -->
        </div>
        <div class="col-12 col-md-6 mb-3 ps-5">
          <div class="d-flex">
            <!-- BROWSER -->
            <div class="col-6 form-group mb-3 me-2">
              <label class="form-label fw-bold w-100 d-block">{{ $t('devices') }}* <span v-b-tooltip.hover :title="$t('info_devices')"><i class="fa fa-info-circle font-size-xRegular"></i></span></label>
              <div class="form-check mb-2">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`browser-mobile`"
                  value="MOBILE"
                  v-model="form.browser"
                >
                <label class="form-check-label" :for="`browser-mobile`">{{ $t('mobile') }}</label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`browser-desktop`"
                  value="DESKTOP"
                  v-model="form.browser"
                >
                <label class="form-check-label" :for="`browser-desktop`">{{ $t('desktop') }}</label>
              </div>
            </div> <!-- END BROWSER -->
            <!-- ENVIRONMENT -->
            <div class="col-6 form-group mb-5 me-2">
              <label class="form-label fw-bold w-100 d-block">{{ $t('environment') }}* <span v-b-tooltip.hover :title="$t('info_environment')"><i class="fa fa-info-circle font-size-xRegular"></i></span></label>
              <div class="form-check mb-2">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`environment-web`"
                  value="WEB"
                  v-model="form.environment"
                >
                <label class="form-check-label" :for="`environment-web`">{{ $t('environment-web') }}</label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`environment-app`"
                  value="APP"
                  v-model="form.environment"
                >
                <label class="form-check-label" :for="`environment-app`">{{ $t('environment-app') }}</label>
              </div>
            </div> <!-- END ENVIRONMENT -->
          </div>
          <!-- LOOKALIKE GLOBAL -->
          <div class="col-6 form-group mb-5">
            <label class="form-label fw-bold w-100 d-block">
              {{ $t('lookalike') }}
              <span
                v-b-tooltip.hover
                :title="$t('info_lookalike')"
              ><i class="fa fa-info-circle font-size-xRegular"></i></span>
            </label>
            <div class="form-check mb-2">
              <input
                class="form-check-input"
                type="checkbox"
                id="lookalike-global"
                v-model="form.lookalike"
                :disabled="form.platform_audiences.length === 0"
              >
              <label class="form-check-label" for="lookalike-global">
                {{ form.lookalike ? $t('deactivate_lookalike') : $t('activate_lookalike') }}
              </label>
            </div>
            <div
              v-if="form.platform_audiences.length === 0"
              class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2"
            >
              <p class="mb-0">{{ $t('lookalike_warning_select_broad_audience') }}</p>
            </div>
          </div> <!-- END LOOKALIKE GLOBAL -->
        </div>
      </div>
      <div class="separator separator-dashed border-color10 my-5"></div>
      <!-- AUDIENCES -->
      <div class="px-10 mt-5">
        <!-- PREMIUM AUDIENCE LIST -->
        <div class="form-group mb-3">
          <label class="form-label fw-bold w-100 d-block">
            {{ $t('broad_audience_list') }}
            <span v-b-tooltip.hover.html :title="tooltips.platform_audiences"><i class="fa fa-info-circle font-size-xRegular"></i></span>
            <Button @click.prevent="" class="btn-xs btn-color10 py-1 ms-2" v-b-modal="'select-platform-audience-modal'">{{ $t('select_audiences') }}</Button>
          </label>
          <template v-if="form.platform_audiences.length === 0">
            <p>{{ $t('zero_selected_audiences') }}</p>
          </template>
          <template v-else>
            <ul>
              <li class="d-flex justify-content-between align-items-center mb-3 col-lg-10" v-for="(audience, index) in form.platform_audiences" :key="index">
                <span class="col-lg-11">
                  <span class="d-flex align-items-center" v-b-tooltip.hover :title="audience.description">
                    {{ audience.name}} <span class="star-icon ms-2 font-size-xSmall"><i class="fa fa-star"></i></span>
                  </span>
                  <span v-if="index < form.platform_audiences.length - 1" class="mx-2 fw-700 position-absolute text-color10 text-uppercase font-size-xSmall">- {{ $t('or') }} -</span>
                </span>
                <div class="col-lg-1 ms-2">
                  <Button
                    class="btn-xs btn-circle btn-danger font-size-small p-0"
                    @click="destroyAudience(index, 'PLATFORM_DATA')"
                    v-b-tooltip.hover :title="$t('remove')"
                  >
                    <i class="fa fa-trash px-3"></i>
                  </Button>
                </div>
              </li>
            </ul>
          </template>
          <SelectAudienceModal
            :selected="form.platform_audiences"
            type="PLATFORM_DATA"
            @audiences-selected="handleAudienceListSelected(...arguments, 'PLATFORM_DATA')"
          />
        </div> <!-- END PREMIUM AUDIENCE LIST -->
        <div class="separator separator-dashed separator-content border-dark my-5"><span class="fw-700 text-color10 text-uppercase">{{ $t('or') }}</span></div>

        <!-- COMBINED AUDIENCE GROUPS -->
        <div class="form-group mb-10">
          <label class="form-label fw-bold w-100 d-block">
            {{ $t('audience_groups') }} <span v-b-tooltip.hover :title="tooltips.audiences"><i class="fa fa-info-circle font-size-xRegular"></i></span>
            <Button @click.prevent="addAudienceGroup" class="btn-xs btn-color10 py-1 ms-2">{{ $t('add_group') }}</Button>
          </label>
          <template v-if="form.audience_groups.length === 0">
            <p>{{ $t('no_audience_groups_selected') }}</p>
          </template>
          <template v-else>
            <p v-if="form.audience_groups.length > 1" class="fw-700">{{ $t('match_all_of_the_following') }}</p>
            <div v-for="(group, groupIndex) in form.audience_groups" :key="groupIndex" class="mb-5">
              <div class="ps-5 border-start border-dashed border-color8">
                <template v-if="group.length === 0">
                  <p>{{ $t('zero_selected_audiences') }}</p>
                </template>
                <span class="fw-600" v-else>{{ $t('match_at_least_one_of_the_following') }}</span>
                <ul>
                  <li
                    class="d-flex justify-content-between align-items-center my-2 col-lg-10"
                    v-for="(audience, index) in group"
                    :key="index"
                  >
                    <div class="col-lg-11">
                      <p class="mb-0" v-b-tooltip.hover :title="audience.description"><span>{{ audience.name }}</span></p>
                      <span v-if="index < group.length - 1" class="mx-2 fw-700 text-color10 text-uppercase font-size-xSmall">- {{ $t('or') }} -</span>
                    </div>
                    <div class="col-lg-1 ms-2">
                      <Button
                        class="btn-xs btn-circle btn-danger font-size-small p-0"
                        @click="removeAudienceFromGroup(groupIndex, index)"
                        v-b-tooltip.hover :title="$t('remove')"
                      >
                        <i class="fa fa-trash px-3"></i>
                      </Button>
                    </div>
                  </li>
                </ul>

                <div class="d-flex flex-column flex-md-row gap-2">
                  <Button
                    id="third-party-audiences-button"
                    @click.prevent="openAudienceModal(groupIndex, 'THIRD_PARTY')"
                    class="btn-sm btn-color10"
                    :softDisabled="!campaign.is_third_party_audiences_enabled"
                  >
                    {{ $t('add_segmented_audience') }}
                  </Button>
                  <b-tooltip
                    target="third-party-audiences-button"
                    v-if="!campaign.is_third_party_audiences_enabled"
                  >
                    {{ $t('only_broad_audiences_available_for_advertisers_less_than_4_days') }}
                  </b-tooltip>
                  <Button @click.prevent="openAudienceModal(groupIndex, 'EMAIL_AUDIENCE')" class="btn-sm btn-color9" v-if="campaign.is_custom_audiences_enabled">
                    {{ $t('add_custom_audience') }}
                  </Button>
                  <Button @click.prevent="removeAudienceGroup(groupIndex)" class="btn-sm btn-danger">
                    {{ $t('remove_group') }}
                  </Button>
                </div>
              </div>
              <!-- AND Separator between groups -->
              <div class="my-4" v-if="groupIndex < form.audience_groups.length - 1">
                <span class="fw-bold text-color10 text-uppercase">{{ $t('and') }}</span>
              </div>
            </div>
          </template>

          <template v-if="!campaign.is_custom_audiences_enabled">
            <div class="mb-2 alert-message-card text-color2 font-size-small px-3 py-2">
              <p class="mb-0">{{ $t('own_audiences_only_available_for_advertisers_over_3_months') }}</p>
            </div>
          </template>
          <template v-if="!campaign.is_third_party_audiences_enabled">
            <div class="mb-2 alert-message-card text-color2 font-size-small px-3 py-2">
              <p class="mb-0">{{ $t('only_broad_audiences_available_for_advertisers_less_than_4_days') }}</p>
            </div>
          </template>
        </div>

        <SelectAudienceModal
          :selected="form.audiences"
          type="THIRD_PARTY"
          v-if="campaign.is_third_party_audiences_enabled"
          @audiences-selected="handleAudienceListSelected(...arguments, 'THIRD_PARTY')"
        />
        <SelectCustomTargetingModal
          :advertiser="campaign.advertiser"
          modalId="select-custom-targeting-emails-modal"
          type="EMAIL_AUDIENCE"
          @custom-targeting-selected="handleCustomAudienceSelected"
        />
      </div>
      <div class="px-10">
        <p :class="{
          'mb-0': audienceCount > 0
        }" class="text-center text-uppercase"><strong>{{ $t('campaign_line_audience_limit_title', { audienceCount: audienceCount,  audienceCountMax: 10 }) }}</strong> <span v-b-tooltip.hover :title="$t('limit_of_broad_and_segmented_audiences')"><i class="fa fa-info-circle font-size-xRegular"></i></span></p>
        <div v-if="audienceCount > 10" class="fw-bold error-message-card text-white font-size-xSmall my-2">
          <span v-html="$t('campaign_line_audience_limit_error.0', { audienceCountMax: 10 })">
          </span>
          <a href='#' @click.prevent='' class='text-white' v-b-modal.support-modal><strong>{{ $t('campaign_line_audience_limit_error.1') }}</strong></a>
          {{ $t('campaign_line_audience_limit_error.2') }}
        </div>
        <div class="d-flex align-items-start justify-content-center mt-3">
          <button class="chat-button py-3" @click="suggestedAudiences = true" v-b-tooltip.hover :title="$t('v2_beta_warning_ia')">
            <img src="/images/v2/layout/pixel-intelligence-icone.png" width="15" alt=""> <span class="d-none d-md-inline-block ms-md-2" v-if="!suggestedAudiences">{{ $t('suggested_audiences') }}</span>
          </button>
          <AudienceSuggestionAgent
            v-if="suggestedAudiences"
            @audiences="handleSuggestedAudiences"
          />
        </div>
      </div><!-- AUDIENCES -->
      <div class="separator separator-dashed border-color10 my-5"></div>
      <!-- BLACKLIST -->
      <div class="form-group mb-3">
        <label class="form-label fw-bold w-100 d-block">
          {{ $t('custom_targeting_type.blacklist') }}
          <span v-b-tooltip.hover :title="tooltips.site_lists"><i class="fa fa-info-circle font-size-xRegular"></i></span>
          <a href="#" @click.prevent="" class="fw-normal ms-2" v-b-modal.select-custom-targeting-blacklist-modal>{{ $t('edit') }}</a>
        </label>
        <template v-if="form.blacklist.length === 0">
          <p>{{ $t('zero_lists_selected') }}</p>
        </template>
        <template v-else>
          <ul>
            <li class="d-flex justify-content-between align-items-center mb-3 col-lg-7" v-for="(list, index) in form.blacklist" :key="index">
              <span>
                {{ list.name}}
              </span>
              <div>
                <Button
                  class="btn-xs btn-circle btn-danger font-size-small p-0"
                  @click="destroyCustomTargetings(index, 'blacklist')"
                  v-b-tooltip.hover :title="$t('remove')"
                >
                  <i class="fa fa-trash px-3"></i>
                </Button>
              </div>
            </li>
          </ul>
        </template>
        <SelectCustomTargetingModal
          :advertiser="campaign.advertiser"
          modalId="select-custom-targeting-blacklist-modal"
          :type="['SITE_LIST', 'APP_LIST']"
          @custom-targeting-selected="handleBlacklistSelected"
        />
      </div> <!-- END BLACKLIST -->
      <div class="separator separator-dashed border-color10 my-5"></div>
      <!-- WISHLIST -->
      <div class="form-group mb-3">
        <label class="form-label fw-bold w-100 d-block">
          {{ $t('custom_targeting_type.wishlist') }}
          <span v-b-tooltip.hover :title="tooltips.site_lists"><i class="fa fa-info-circle font-size-xRegular"></i></span>
          <a href="#" @click.prevent="" class="fw-normal ms-2" v-b-modal.select-custom-targeting-wishlist-modal>{{ $t('edit') }}</a>
        </label>
        <template v-if="form.wishlist.length === 0">
          <p>{{ $t('zero_lists_selected') }}</p>
        </template>
        <template v-else>
          <ul>
            <li class="d-flex justify-content-between align-items-center mb-3 col-lg-7" v-for="(list, index) in form.wishlist" :key="index">
              <span>
                {{ list.name}}
              </span>
              <div>
                <Button
                  class="btn-xs btn-circle btn-danger font-size-small p-0"
                  @click="destroyCustomTargetings(index, 'wishlist')"
                  v-b-tooltip.hover :title="$t('remove')"
                >
                  <i class="fa fa-trash px-3"></i>
                </Button>
              </div>
            </li>
          </ul>
        </template>
        <SelectCustomTargetingModal
          :advertiser="campaign.advertiser"
          modalId="select-custom-targeting-wishlist-modal"
          type="SITE_LIST"
          :withGlobal="true"
          @custom-targeting-selected="handleWishlistSelected"
        />
      </div> <!-- END WISHLIST -->
      <div class="separator separator-dashed border-color10 my-5"></div>
      <!-- ADDITIONAL SETTINGS -->
      <div>
        <div class="mb-4">
          <Button
            class="btn-clean text-none fw-bold cursor-pointer"
            @click="isAdditionalSettingsOpen = !isAdditionalSettingsOpen"
          >
            <span v-if="!isAdditionalSettingsOpen">
              <i class="fa fa-angle-right fw-bold"></i>
            </span>
            <span v-else>
              <i class="fa fa-angle-down fw-bold"></i>
            </span>
            {{ $t('additional_settings') }}
          </Button>
          <div class="mt-3" v-if="isAdditionalSettingsOpen">
            <!-- FREQUENCY CAP -->
            <div>
              <label class="form-label fw-bold w-100 d-block">{{ $t('frequency_cap') }}</label>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  :id="`frequency-type-unlimited`"
                  value="UNLIMITED"
                  v-model="form.frequency_cap.type"
                >
                <label class="form-check-label" :for="`frequency-type-unlimited`">Sem limite</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  :id="`frequency-type-limited`"
                  value="LIMITED"
                  v-model="form.frequency_cap.type"
                >
                <label class="form-check-label" :for="`frequency-type-limited`">Definir limite</label>
              </div>
              <!-- FREQUENCY CAP EXPLANATION -->
              <div
              class="my-2 alert-message-card text-color2 font-size-small px-3 py-2"
              >
                <p
                  class="mb-0"
                  v-if="form.frequency_cap.type === 'LIMITED'"
                  v-html="$t('frequency_cap_limited_explanation')"
                ></p>
                <p
                  class="mb-0"
                  v-if="form.frequency_cap.type === 'UNLIMITED'"
                >
                  {{ $t('frequency_cap_unlimited_explanation') }}
                </p>
              </div> <!-- END FREQUENCY CAP EXPLANATION-->
              <div class="mt-3" v-if="form.frequency_cap.type === 'LIMITED'">
                <div class="d-flex align-items-center">
                  <div class="col-lg-3">
                    <span>{{ $t('frequency_cap_input.0') }}</span>
                  </div>
                  <div class="col-lg-2 px-2">
                    <input
                      type="text"
                      class="form-control text-center"
                      placeholder="10"
                      :disabled="form.frequency_cap.type != 'LIMITED'"
                      v-model.trim="form.frequency_cap.max_impressions"
                    />
                  </div>
                  <div class="col-lg-2 px-2">
                    <span>{{ $t('frequency_cap_input.1') }}</span>
                  </div>
                  <div class="col-lg-2">
                    <input
                    type="text"
                    class="form-control text-center"
                    placeholder="5"
                    :disabled="form.frequency_cap.type != 'LIMITED' || form.frequency_cap.time_unit === 'MONTHS'"
                    v-model.trim="form.frequency_cap.time_unit_count"
                  />
                  </div>
                  <div class="col-lg-3 ps-3">
                    <div class="custom-select">
                      <select
                        class="form-control"
                        :disabled="form.frequency_cap.type != 'LIMITED'"
                        v-model="form.frequency_cap.time_unit"
                        @change="handleFrequencyCapTimeUnitSelected"
                      >
                        <option value="">{{ $t('select') }}</option>
                        <option value="MINUTES">{{ $t('frequency_cap_time_unit.MINUTES') }}</option>
                        <option value="HOURS">{{ $t('frequency_cap_time_unit.HOURS') }}</option>
                        <option value="DAYS">{{ $t('frequency_cap_time_unit.DAYS') }}</option>
                        <option value="WEEKS">{{ $t('frequency_cap_time_unit.WEEKS') }}</option>
                        <option value="MONTHS">{{ $t('frequency_cap_time_unit.MINUTES') }}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <!-- MESSAGES -->
              <div v-if="form.frequency_cap.type === 'LIMITED'">
                <div
                  class="my-2 alert-message-card text-color2 font-size-small px-3 py-2"
                  v-if="form.frequency_cap.time_unit === 'MINUTES'"
                >
                    <p class="mb-0">
                      {{ $t('frequency_cap_time_unit_count_minutes_rule') }}
                    </p>
                </div>
                <div
                  class="my-2 alert-message-card text-color2 font-size-small px-3 py-2"
                  v-if="form.frequency_cap.time_unit === 'HOURS'"
                >
                    <p class="mb-0">
                      {{ $t('frequency_cap_time_unit_count_hours_rule') }}
                    </p>
                </div>
                <div
                  class="my-2 alert-message-card text-color2 font-size-small px-3 py-2"
                  v-if="form.frequency_cap.time_unit === 'DAYS'"
                >
                    <p class="mb-0">
                      {{ $t('frequency_cap_time_unit_count_days_rule') }}
                    </p>
                </div>
                <div
                  class="my-2 alert-message-card text-color2 font-size-small px-3 py-2"
                  v-if="form.frequency_cap.time_unit === 'WEEKS'"
                >
                    <p class="mb-0">
                      {{ $t('frequency_cap_time_unit_count_weeks_rule') }}
                    </p>
                </div>
                <div
                  class="my-2 alert-message-card text-color2 font-size-small px-3 py-2"
                  v-if="form.frequency_cap.max_impressions > 50 && form.frequency_cap.max_impressions > 0 &&
                  ['MINUTES', 'HOURS', 'DAYS'].includes(form.frequency_cap.time_unit)"
                >
                    <p class="mb-0">
                      {{ $t('frequency_cap_high_impressions_warning') }}
                    </p>
                </div>
                <div
                  class="my-2 alert-message-card text-color2 font-size-small px-3 py-2"
                  v-if="form.frequency_cap.max_impressions < 5  && form.frequency_cap.max_impressions > 0 &&
                  ['WEEKS', 'MONTHS'].includes(form.frequency_cap.time_unit)"
                >
                    <p class="mb-0">
                      {{ $t('frequency_cap_low_impressions_warning') }}
                    </p>
                </div>
              </div> <!-- END MESSAGES -->
            </div> <!-- END FREQUENCY CAP -->
          </div>
        </div>
      </div> <!-- END ADDITIONAL SETTINGS -->
      <div class="separator separator-dashed border-color10 my-5"></div>
      <!-- CREATIVES -->
      <div class="px-10">
        <!-- CREATIVES HEADER -->
        <div
          class="mb-4"
        >
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h3 class="text-uppercase font-size-regular fw-500">
                <span>{{ $t('creatives') }}</span> <span v-b-tooltip.hover :title="tooltips.creatives"><i class="fa fa-info-circle font-size-xRegular"></i></span>
              </h3>
            </div>
          </div>
          <p class="font-size-xSmall fw-bold mt-2" v-if="form.type === 'DISPLAY'">{{ $t('mandatory_creative_300x250') }}</p>
        </div> <!-- END CREATIVES HEADER -->
        <!-- CREATIVE LIST -->
        <p v-if="form.creatives.length === 0">{{ $t('no_creatives_selected') }}</p>
        <div class="col-lg-8 mx-auto" v-else>
          <!-- CREATIVE -->
          <div
            class="d-flex creative-item flex-wrap align-items-center mb-3 py-3 border border-dashed"
            v-for="(creative, index) in form.creatives"
            :key="index"
          >
            <div class="col-lg-3">
              <img :src="creative.file" alt="" style="max-width: 100px;" />
            </div>
            <div class="col-lg-8 text-start">
              <p class="mb-0">{{ creative.name }}</p>
              <p class="mb-0">{{ creative.format | formatCreativeFormat }} | <strong>{{ creative.layout }}</strong></p>
            </div>
            <div class="col-lg-1">
              <Button
                class="btn-xs btn-circle btn-danger font-size-small p-0"
                @click="openDestroyCreativeModal(creative)"
                v-b-tooltip.hover :title="$t('remove_creative')"
              >
                <i class="fa fa-trash px-3"></i>
              </Button>
            </div>
          </div> <!-- END CREATIVE -->
        </div> <!-- END CREATIVE LIST -->
        <DestroyCreativeModal
          :creative="selectedCreative"
          @creative-destroyed="destroyCreative"
        />
        <SelectCreative
          v-if="campaign"
          :advertiserId="campaign.advertiser.id"
          :format="form.type"
          @creative-selected="handleSelectedCreative"
        />
      </div> <!-- END CREATIVES -->
      <!-- ACTIONS -->
      <div class="text-center mt-10">
        <!-- MESSAGES -->
        <div
          class="mb-2 alert-message-card text-color2 font-size-small px-3 py-2"
          v-if="loader"
        >
          <p
            class="mb-0"
          >
            {{ $t('this_action_may_take_a_few_minutes') }}
          </p>
        </div> <!-- END MESSAGES -->
        <Button
          v-if="isCreatePage || isEditPage"
          class="btn btn-color10"
          @click="submit"
          :loading="loader"
          :disabled="!isFormComplete"
        >
          {{ isEditPage ? this.$t('save_strategy') : this.$t('create_strategy') }}
        </Button>
        <Button
          class="btn-color10"
          v-if="!isCreatePage && !isEditPage"
          :disabled="!isFormComplete"
          @click="handleFormLine"
        >
          {{ $t('next') }}
        </Button>
      </div> <!-- END ACTION -->
    </form>
  </div>
</template>

<script>

import CampaignLinesService from '@/modules/campaigns/services/campaign-lines-service'
import CategoriesService from '@/modules/categories/services/categories-service'
import HelpersService from '@/modules/helpers/services/helpers-service'
import variables from '@/mixins/variables'

import AgeSlider from '@/components/common/Slider/AgeSlider'
import AudienceSuggestionAgent from '@/components/chat/AudienceSuggestionAgent'
import Button from '@/components/common/Button/Button'
import DestroyCreativeModal from '@/components/campaigns/UpsertForm/DestroyCreativeModal'
import GoogleMapsComponent from '@/components/google/maps/GoogleMapsComponent'
import Loader from '@/components/common/Loader/Loader'
import Multiselect from 'vue-multiselect'
import SelectAudienceModal from '@/components/audiences/SelectAudienceModal'
import SelectCreative from '@/components/creatives/SelectCreative'
import SelectCustomTargetingModal from '@/components/custom-targetings/SelectCustomTargetingModal'

import { mapActions, mapState } from 'vuex'

export default {
  mixins: [variables],
  props: ['baseCampaign', 'advertiser'],
  components: {
    AgeSlider,
    AudienceSuggestionAgent,
    Button,
    DestroyCreativeModal,
    GoogleMapsComponent,
    Loader,
    Multiselect,
    SelectAudienceModal,
    SelectCreative,
    SelectCustomTargetingModal
  },
  data () {
    return {
      campaign: '',
      campaignLoader: false,
      categories: [],
      categoriesLoader: false,
      cities: [],
      citiesLoader: false,
      countries: [],
      countriesLoader: false,
      currentAudienceGroupIndex: null,
      currentAudienceType: null,
      form: {
        package_id: '',
        marker_locations: [],
        name: '',
        type: 'DISPLAY',
        platform_audiences: [],
        demographics: {
          gender: [],
          age: ['18', '65+'],
          age_unknown: ''
        },
        audiences: [],
        states: [],
        cities: [],
        browser: [],
        environment: [],
        lookalike: false,
        categories: [],
        keywords: [],
        custom_audiences: [],
        radius: 5,
        radius_location: [],
        audience_groups: [],
        wishlist: [],
        blacklist: [],
        creatives: [],
        frequency_cap: {
          type: 'UNLIMITED',
          time_unit: '',
          time_unit_count: '',
          max_impressions: ''
        }
      },
      isAdditionalSettingsOpen: false,
      keywordsOptions: [],
      line: '',
      loader: false,
      segmentation: '',
      selectedCountries: [],
      selectedCreative: '',
      states: [],
      statesLoader: false,
      suggestedAudiences: false,
      useMap: false
    }
  },
  watch: {
    /**
     * Watch for changes to countries
     */
    countries (newValue, oldValue) {
      // If is edit page, set selected countries
      if (this.isEditPage) this.setSelectedCountries()
    },
    /**
     * Watch for changes to selected countries
     */
    selectedCountries (newValue, oldValue) {
      if (!newValue.includes('code')) {
        this.countries.map(country => {
          this.selectedCountries.map(selectedCountry => {
            if (selectedCountry?.short_name === country?.short_name) selectedCountry.code = country.id
          })
        })
      }

      this.getStates()
      this.getCities()
    }
  },
  created () {
    if (this.isEditPage) {
      this.setLine()
    } else {
      if (this.$route.name === 'lines.create') {
        this.campaign = this.baseCampaign
        // If is create page, the package id is on the url
        this.segmentation = this.campaign.packages.find(item => {
          return item.id === this.$route.params.pId
        })
        this.form.package_id = this.segmentation.id

        // Check if there is model to replicate
        if (this.$route.query.replicate) {
          this.setLine(this.$route.query.replicate)
        }
      } else {
        this.campaign = {
          is_third_party_audiences_enabled: this.advertiser.is_third_party_audiences_enabled,
          is_custom_audiences_enabled: this.advertiser.is_custom_audiences_enabled,
          advertiser: this.advertiser
        }
      }
    }
    this.getCountries()
    this.getCategories()
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    /**
     * Audience count
    */
    audienceCount () {
      // Count audiences in groups
      const groupAudiencesCount = this.form.audience_groups.flat().length
      // Platform audience count
      const platformAudiencesCount = this.form.platform_audiences.length

      if (platformAudiencesCount > 0 && groupAudiencesCount > 0) {
        return Math.max(platformAudiencesCount, groupAudiencesCount)
      } else {
        return platformAudiencesCount + groupAudiencesCount
      }
    },
    /**
     * Get categories options
     */
    categoriesOptions () {
      return this.categories.map(item => {
        return {
          label: this.$t('categories.' + item.slug),
          code: item.id
        }
      })
    },
    /**
     * Get cities options based on the selected states of the line
     */
    citiesOptions () {
      /* const states = this.form.states.map(state => {
        return state.label
      }) */
      const cities = this.cities.reduce((filtered, item) => {
        /* if (states.includes(item.state_name)) {
          filtered.push({
            label: `${item.name} (${item.state_short_name})`,
            code: item.id
          })
        } */

        filtered.push({
          label: `${item.name} (${item.state_short_name} - ${item.country_short_name})`,
          code: item.id
        })
        return filtered
      }, [])
      return cities
    },
    /**
     * Get countries options
     */
    countryOptions () {
      return this.countries
        .map(item => {
          return {
            label: `${item.name} (${item.short_name})`,
            code: item.id
          }
        })
    },
    /**
     * Check if campaign has proximity location
     */
    hasProximityLocation () {
      if (this.campaign) {
        if (this.campaign.marker_locations.length > 0) return true
      }
      return this.form.marker_locations.length > 0
    },
    /**
     * Check if is create page
     */
    isCreatePage () {
      return this.$route.name === 'lines.create'
    },
    /**
     * Check if is edit page
     */
    isEditPage () {
      return this.$route.name === 'lines.edit'
    },
    /**
     * Is form complete
     */
    isFormComplete () {
      let complete = true
      if (!this.form.name ||
        this.form.browser.length === 0 ||
        this.form.environment.length === 0 ||
        this.form.demographics.gender.length === 0 ||
        this.form.demographics.age.length === 0 ||
        this.form.creatives.length === 0
      ) complete = false

      let format = null
      let hasValidLayout = false
      this.form.creatives.forEach(creative => {
        if (!format) {
          // Define the format as the first creative
          format = creative.format
        } else if (creative.format !== format) {
          // If any creatives have different format, it's invalid
          complete = false
        }

        // If is DISPLAY, check if has any 300x250 banners
        if (creative.format === 'DISPLAY' && creative?.layout.toLowerCase() === '300x250') {
          hasValidLayout = true
        }
      })

      // Check if frequency cap is limited and it's rules
      if (this.form.frequency_cap.type === 'LIMITED') {
        // Check if everything is filled out
        if (!this.form.frequency_cap.time_unit || !this.form.frequency_cap.time_unit_count || !this.form.frequency_cap.max_impressions) complete = false

        const count = this.form.frequency_cap.time_unit_count
        if (count < 1) complete = false
        // Check rules for time unit count
        if (this.form.frequency_cap.time_unit === 'MINUTES' && count > 59) complete = false
        if (this.form.frequency_cap.time_unit === 'HOURS' && count > 23) complete = false
        if (this.form.frequency_cap.time_unit === 'DAYS' && count > 6) complete = false
        if (this.form.frequency_cap.time_unit === 'WEEKS' && count > 4) complete = false
        if (this.form.frequency_cap.time_unit === 'MONTHS' && count !== 1) complete = false
      }

      if (format === 'DISPLAY' && !hasValidLayout) {
        complete = false
      }

      return complete
    },
    markerLocations () {
      let markerLocations
      if (this.form.marker_locations.length > 0) markerLocations = this.form.marker_locations
      // if (this.campaign && this.campaign.marker_locations.length > 0) markerLocations = this.campaign.marker_locations
      return markerLocations ?? null
    },
    /**
     * Get page title
     */
    pageTitle () {
      return this.$route.name === 'lines.create' ? this.$t('create_strategy') : this.$t('edit_strategy')
    },
    /**
     * show alert on cities and states selection
     */
    showAlertOnCitiesAndStatesSelection () {
      const selectedStates = this.form.states.map((state) => {
        const stateData = this.states.find((s) => s.id === state.code)
        return stateData?.name || ''
      })

      const selectedCities = this.form.cities.map((city) => {
        const cityData = this.cities.find((c) => c.id === city.code)
        return cityData ? { name: cityData.name, state_name: cityData.state_name } : null
      }).filter(city => city !== null)

      if (selectedStates.length && selectedCities.length) {
        // Check if the cities are within the selected state
        const citiesOutOfState = selectedCities.filter((city) =>
          !selectedStates.includes(city.state_name)
        )

        if (citiesOutOfState.length) {
          return this.$t('region_selection_states_and_cities', {
            states: selectedStates.join(', '),
            cities: [...new Set(citiesOutOfState.map((city) => city.state_name))].join(', ')
          })
        } else {
          return this.$t('region_selection_city_inside_state')
        }
      }

      if (selectedStates.length > 0) {
        return this.$t('region_selection_only_states')
      }

      if (selectedCities.length > 0) {
        return this.$t('region_selection_only_cities')
      }
      return false
    },
    /**
     * State options for select 2 field
     */
    stateOptions () {
      return this.states.map(state => {
        return {
          label: `${state.name} (${state.country_short_name})`,
          code: state.id
        }
      })
    }
  },
  methods: {
    ...mapActions('campaigns', [
      'setCurrentCampaign'
    ]),
    /**
     * add audience group
     */
    addAudienceGroup () {
      this.form.audience_groups.push([])
    },
    /**
     * add keywords
     */
    addKeywords (newKeyword) {
      const keyword = {
        label: newKeyword,
        code: newKeyword.substring(0, 2) + Math.floor((Math.random() * 10000000))
      }
      this.keywordsOptions.push(keyword)
      this.form.keywords.push(keyword)
    },
    /**
     * Clear form property based on a given parameter
     */
    clearSelection (selection) {
      this.form[selection] = []
    },
    /**
     * Destroy audience
     */
    destroyAudience (index, type) {
      if (type === 'PLATFORM_DATA') this.form.platform_audiences.splice(index, 1)
      else this.form.audiences.splice(index, 1)
    },
    /**
     * Destroy creative
     */
    destroyCreative (creative) {
      const index = this.form.creatives.findIndex(item => {
        return item.id === creative.id
      })
      if (index < 0) return
      this.form.creatives.splice(index, 1)
    },
    /**
     * Destroy custom targetings
     */
    destroyCustomTargetings (index, type, targetAudience = null) {
      if (type === 'blacklist') {
        this.form.blacklist.splice(index, 1)
      } else if (type === 'wishlist') {
        this.form.wishlist.splice(index, 1)
      } else if (type === 'custom_audiences') {
        this.form.custom_audiences.splice(index, 1)
      }
    },
    /**
     * Get categories
     */
    async getCategories () {
      this.categoriesLoader = true
      try {
        const categories = await CategoriesService.getCategories({
          type: 'CAMPAIGN',
          campaign_type: 'PROGRAMMATIC'
        })
        this.categories = categories.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.categoriesLoader = false
    },
    /**
     * Get cities
     */
    async getCities () {
      this.citiesLoader = true
      try {
        const cities = await HelpersService.getCities({
          countryId: this.selectedCountries.map(item => {
            return item.code
          })
        })
        this.cities = cities
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.citiesLoader = false
    },
    /**
     * Get countries
     */
    async getCountries () {
      this.countriesLoader = true
      try {
        const countries = await HelpersService.getCountries()
        this.countries = countries
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.countriesLoader = false
    },
    /**
     * Get states
     */
    async getStates () {
      this.statesLoader = true
      try {
        const states = await HelpersService.getStates({
          country_id: this.selectedCountries.map(item => {
            return item.code
          })
        })
        this.states = states

        const brazilSelected = this.selectedCountries.some(
          country => country.label.includes('(BR)')
        )

        if (
          brazilSelected &&
          !this.isEditPage &&
          this.stateOptions.length > 0
        ) {
          this.selectAllStates()
        }
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.statesLoader = false
    },
    /**
     * Handle audience list selected
     */
    handleAudienceListSelected (audiences, type) {
      if (type === 'PLATFORM_DATA') this.form.platform_audiences = [...audiences]
      if (this.currentAudienceGroupIndex === null) return

      const group = this.form.audience_groups[this.currentAudienceGroupIndex]

      audiences.forEach(aud => {
        const alreadyExists = group.some(item => item.id === aud.id && item.type === type)
        if (!alreadyExists) {
          group.push({
            id: aud.id,
            name: aud.name,
            type: type,
            group_index: this.currentAudienceGroupIndex,
            description: aud.description
          })
        }
      })

      this.currentAudienceGroupIndex = null // reset
    },
    /**
     * Handle blacklist selected
     */
    handleBlacklistSelected (list) {
      const index = this.form.blacklist.findIndex(item => {
        return item.id === list.id
      })
      if (index >= 0) return
      this.form.blacklist.push(list)
    },
    /**
     * Handle custom audience selected
     */
    handleCustomAudienceSelected (customAudienceList) {
      if (this.currentAudienceGroupIndex === null) return

      const group = this.form.audience_groups[this.currentAudienceGroupIndex]

      // Garantir que é array
      const list = Array.isArray(customAudienceList) ? customAudienceList : [customAudienceList]

      list.forEach(aud => {
        const alreadyExists = group.some(item => item.id === aud.id && item.type === 'EMAIL_AUDIENCE')
        if (!alreadyExists) {
          group.push({
            id: aud.id,
            name: aud.name,
            type: aud.type,
            description: aud.description,
            group_index: this.currentAudienceGroupIndex
          })
        }
      })

      this.currentAudienceGroupIndex = null // reset
    },
    /**
     * handle form line
     */
    handleFormLine () {
      const form = this.prepareForm()
      this.$emit('form-line', form)
    },
    /**
     * Handle frequency cap time unit selected
    */
    handleFrequencyCapTimeUnitSelected () {
      if (this.form.frequency_cap.time_unit === 'MONTHS') this.form.frequency_cap.time_unit_count = 1
    },
    /**
     * Handle selected creative
     */
    handleSelectedCreative (creatives) {
      // Check if creative is not already selected
      creatives.forEach(creative => {
        const index = this.form.creatives.findIndex(item => {
          return item.id === creative.id
        })
        if (index < 0) this.form.creatives.push(creative)
      })
    },
    /**
     * Handle suggested audiences
     */
    handleSuggestedAudiences (audiences) {
      // Utility function to collect all existing UUIDs
      const collectExistingUUIDs = () => {
        const uuids = new Set()

        this.form.audience_groups.forEach(group =>
          group.forEach(aud => uuids.add(aud.id))
        )

        this.form.platform_audiences.forEach(aud =>
          uuids.add(aud.id)
        )

        return uuids
      }

      const existingUUIDs = collectExistingUUIDs()
      const newThirdPartyGroup = []
      const nextGroupIndex = this.form.audience_groups.length > 0 ? this.form.audience_groups.length : 0

      audiences.forEach(({ payload }) => {
        const { uuid, name, type, description } = payload

        // Ignora se já existe
        if (existingUUIDs.has(uuid)) return

        // Formata audiência comum
        const baseAudience = { id: uuid, name }

        if (type === 'Third Party') {
          if (!this.advertiser.is_third_party_audiences_enabled) {
            return
          }
          newThirdPartyGroup.push({
            ...baseAudience,
            type: 'THIRD_PARTY',
            description,
            group_index: nextGroupIndex
          })
        } else {
          this.form.platform_audiences.push({
            ...baseAudience,
            type: 'PLATFORM_DATA',
            description,
            pixel: null
          })
        }

        existingUUIDs.add(uuid)
      })

      // Add a new group if there is at least one valid audience.
      if (newThirdPartyGroup.length > 0) {
        this.form.audience_groups.push(newThirdPartyGroup)
      }
    },
    /**
     * Handle wishlist selected
     */
    handleWishlistSelected (list) {
      const index = this.form.wishlist.findIndex(item => {
        return item.id === list.id
      })
      if (index >= 0) return
      this.form.wishlist.push(list)
    },
    /**
     * Open destroy creative modal
     */
    openDestroyCreativeModal (creative) {
      this.selectedCreative = creative
      this.$bvModal.show('destroy-creative-modal')
    },
    /**
     * open audience modal
     */
    openAudienceModal (groupIndex, type) {
      this.currentAudienceGroupIndex = groupIndex
      this.currentAudienceType = type

      if (type === 'THIRD_PARTY') {
        this.$bvModal.show('select-audience-modal') // já existe
      } else if (type === 'EMAIL_AUDIENCE') {
        this.$bvModal.show('select-custom-targeting-emails-modal') // já existe
      }
    },
    /**
     * prepare form
     */
    prepareForm () {
      //  remove circles from map object, to avoid circular error on JsonParse
      if (this.form.marker_locations.length > 0) {
        this.form.marker_locations.forEach((location) => {
          delete location.circle
        })
      } else {
        this.form.radius = null
      }

      // Create copy of form that can be changed (object assign wont work for multi-layered objects)
      const form = JSON.parse(JSON.stringify(this.form))
      if (this.form.platform_audiences.length === 0) form.lookalike = false

      if (this.form.demographics.age_unknown) {
        if (!this.form.demographics.age.includes('UNKNOWN')) {
          delete form.demographics.age_unknown
          form.demographics.age.push('UNKNOWN')
        }
      }

      form.audiences = [] // THIRD_PARTY
      form.custom_audiences = [] // EMAIL_AUDIENCE e FIRST_PARTY
      form.platform_audiences = [] // PLATFORM_DATA

      // Populate platform_audiences from the direct array
      this.form.platform_audiences.forEach(aud => {
        form.audiences.push({
          id: aud.id,
          group_index: 0
        })
      })

      if (this.form.audience_groups.some(group => group.length > 0)) {
        this.form.audience_groups.forEach((group, groupIdx) => {
          group.forEach(aud => {
            const audienceData = {
              id: aud.id,
              group_index: groupIdx
            }

            if (aud.type === 'THIRD_PARTY' || aud.type === 'FIRST_PARTY') {
              form.audiences.push(audienceData)
            } else if (aud.type === 'EMAIL_AUDIENCE') {
              form.custom_audiences.push(audienceData)
            }
          })
        })
      }
      // Map UI field (lookalike) to the API field (targetingExpansion)
      // Also enable automatically when Google (PLATFORM_DATA) audiences are selected
      const hasPlatformAudiences = this.form.platform_audiences.length > 0
      form.targetingExpansion = form.lookalike === true || hasPlatformAudiences
      delete form.lookalike

      delete form.audience_groups
      delete form.platform_audiences

      form.blacklist = this.form.blacklist.map(item => {
        return item.id
      })

      form.wishlist = this.form.wishlist.map(item => {
        return item.id
      })

      form.creatives = this.form.creatives.map(item => {
        return item.id
      })

      form.cities = this.form.cities.map(item => {
        return item.code
      })

      form.states = this.form.states.map(state => {
        return state.code
      })

      form.categories = this.form.categories.map(category => {
        return category.code
      })

      form.keywords = this.form.keywords.map(keyword => {
        return keyword.label
      })

      return form
    },
    /**
     * remove audience group
     */
    removeAudienceGroup (groupIndex) {
      this.form.audience_groups.splice(groupIndex, 1)
    },
    /**
     * remove audience from group
     */
    removeAudienceFromGroup (groupIndex, audienceIndex) {
      this.form.audience_groups[groupIndex].splice(audienceIndex, 1)
    },
    /**
     * Select all states
     */
    selectAllStates () {
      const all = []
      this.stateOptions.forEach(item => {
        all.push({
          label: item.label,
          code: item.code
        })
      })
      this.form.states = all
    },
    /**
     * Set line (if there is ID, should set a specific line)
     */
    setLine (id = null) {
      this.campaign = this.baseCampaign
      this.segmentation = this.campaign.packages.find(item => {
        return item.lines.find(line => {
          if (id) return line.id === id
          else return line.id === this.$route.params.id
        })
      })
      this.form.package_id = this.segmentation.id

      const line = this.segmentation.lines.find(item => {
        if (id) return item.id === id
        else return item.id === this.$route.params.id
      })

      if (line.marker_locations.length > 0) {
        this.useMap = true
        this.form.marker_locations = line.marker_locations
      }
      // Set line
      if (!id) this.line = line

      this.form.name = line.name
      this.form.type = line.type
      this.form.browser = line.browser
      this.form.environment = line.environment
      this.form.lookalike = line.targeting_expansion ?? false

      if (Object.keys(line.demographics).length > 0) {
        this.form.demographics.age = [line.demographics.age[0], line.demographics.age[1]]
        this.form.demographics.age_unknown = line.demographics.age[2] ?? []
        this.form.demographics.gender = line.demographics.gender ?? []
      }

      this.form.states = line.states.map(s => {
        return {
          label: `${s.name} (${s.country_short_name})`,
          code: s.id
        }
      })
      this.form.cities = line.cities.map(c => {
        return {
          label: `${c.name} (${c.state_short_name} - ${c.country_short_name})`,
          code: c.id
        }
      })
      this.form.categories = line.categories.map(c => {
        return {
          label: this.$t('categories.' + c.slug),
          code: c.id
        }
      })
      this.form.keywords = line.keywords.map(k => {
        return {
          label: k.text,
          code: k.id
        }
      })

      this.form.audience_groups = []
      this.form.platform_audiences = []

      // Distributes audiences from 'line' to new 'audience_groups' or 'platform_audiences'
      const tempAudienceGroups = {}
      line.audiences.forEach(aud => {
        if (aud.type === 'PLATFORM_DATA') {
          this.form.platform_audiences.push(aud)
        } else {
          const groupIndex = aud.group_index !== undefined ? aud.group_index : 0
          if (!tempAudienceGroups[groupIndex]) {
            tempAudienceGroups[groupIndex] = []
          }
          tempAudienceGroups[groupIndex].push(aud)
        }
      })
      if (this.campaign.is_custom_audiences_enabled && line.custom_audiences) {
        line.custom_audiences.forEach(aud => {
          const groupIndex = aud.group_index !== undefined ? aud.group_index : 0
          if (!tempAudienceGroups[groupIndex]) {
            tempAudienceGroups[groupIndex] = []
          }
          tempAudienceGroups[groupIndex].push(aud)
        })
      }
      Object.keys(tempAudienceGroups).sort((a, b) => a - b).forEach(groupIndex => {
        const groupAudiences = tempAudienceGroups[groupIndex]
        this.form.audience_groups.push(groupAudiences)
      })

      this.form.blacklist = line.blacklist
      this.form.wishlist = line.wishlist
      this.form.frequency_cap.type = line.frequency_cap.type
      this.form.frequency_cap.time_unit = line.frequency_cap.time_unit
      this.form.frequency_cap.time_unit_count = line.frequency_cap.time_unit_count
      this.form.frequency_cap.max_impressions = line.frequency_cap.max_impressions
      this.form.creatives = line.creatives.map(c => {
        return {
          id: c.id,
          name: c.name,
          format: c.format,
          layout: c.layout,
          url: c.url,
          is_desktop: c.is_desktop,
          is_mobile: c.is_mobile,
          is_app: c.is_app,
          file: c.file
        }
      })

      // Set selected countries
      this.setSelectedCountries()
    },
    /**
     * Set selected countries
     */
    setSelectedCountries () {
      if (!this.line) return
      const countries = []
      this.line.states.forEach(item => {
        if (!countries.includes(item.country_short_name)) countries.push(item.country_short_name)
      })
      this.line.cities.forEach(item => {
        if (!countries.includes(item.country_short_name)) countries.push(item.country_short_name)
      })

      this.line.marker_locations.forEach(item => {
        if (!countries.includes(item.country_short_name)) countries.push(item.country_short_name)
      })

      this.selectedCountries = this.countries.filter(item => {
        return countries.includes(item.short_name)
      }).map(item => {
        return {
          label: `${item.name} (${item.short_name})`,
          code: item.id
        }
      })
    },
    /**
     * Submit
     */
    async submit () {
      this.loader = true
      try {
        const form = this.prepareForm()

        let line = null
        if (!this.line) {
          line = await CampaignLinesService.createCampaignLine(form)
        } else {
          line = await CampaignLinesService.updateCampaignLine(this.line.id, form)
        }
        this.setCurrentCampaign({ campaign: '' })
        this.$router.push({ name: 'lines.show', params: { cId: this.campaign.id, id: line.id } })
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    toggleMap (answer) {
      this.useMap = answer
      this.form.radius = 5
      if (!answer) {
        this.form.radius = null
        this.form.marker_locations = []
      } else {}
    },
    /**
      * Updates radius value from child component emitted event
      * @param value
      */
    updateRadius (value) {
      this.form.radius = value
    },
    /**
    * Updates radius coordinate edges from child component emitted event
    * @param value
    */
    updateRadiusLocation (value) {
      this.form.radius_location = value
    },
    /**
     * Updates marked locations coordinates from child component emitted event
     * @param value
     */
    updateMarkerLocations (value) {
      if (value.length === 0) {
        this.form.marker_locations = value
        return
      }

      //  Mapping variable first to avoid Converting circular structure to JSON error
      //  Caused by assigning a reference value to variable
      this.form.marker_locations = value.map(marker => ({
        lat: marker.lat,
        lng: marker.lng,
        city: marker.city ?? null,
        state: marker.state ?? null,
        country_short_name: marker?.country?.short_name ?? null,
        name: marker.name,
        radius: marker.radius,
        circle: marker.circle ?? null
      }))

      //  Find country and get it's properties to add it to selectedCountries
      const lastIndex = value.length - 1

      if (this.countryOptions.length > 0) {
        //  Search country information from marker_locations array
        const selectedCountry = this.countryOptions.find(countryOption => {
          if (value[lastIndex].country && countryOption.label.includes(value[lastIndex].country.long_name)) return true
        })

        if (this.selectedCountries.length === 0) this.selectedCountries.push(selectedCountry)
        //  checking if Country is already selected, avoiding repeated values
        else if (selectedCountry) {
          if (this.selectedCountries.length > 0) {
            const countryAlreadySelected = this.selectedCountries.map(country => {
              return country.code === selectedCountry.code
            })
            if (!countryAlreadySelected) this.selectedCountries.push(selectedCountry)
          }
        }
      }
    }
  }
}

</script>

<style lang="css" src="../../../../node_modules/vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
  .btn-geography-select {
    border: 1px dashed $color10 !important;
    max-width: 250px;

    &.selected {
      background: $color10 !important;
      color: #fff;
    }

    .description {
      text-transform: none;
    }
  }

  .geography-container-disabled {
    opacity: 0.2;
    pointer-events: none;
  }

  .tooltip .tooltip-inner {
    max-width: 300px;
  }
</style>
